import { useCallback, useEffect, useRef, useState } from 'react'
import eventBus from '../eventBus'
import { IMedia } from '../../shared/MediaPreview/mediaPreview.types.ts'

const useMediaPreview = () => {
  const loaderRef = useRef<React.ReactNode>(null)
  const mediasRef = useRef<IMedia[]>([])
  const maxLength = mediasRef.current.length - 1
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isActive, setIsActive] = useState(false)

  useEffect(() => {
    const unsubscribe = eventBus.sub('mediaPreview.request', (event) => {
      loaderRef.current = event.loader
      mediasRef.current = event.medias
      setIsActive(true)
      setCurrentIndex(event.index ?? 0)
    })

    return () => {
      unsubscribe()
    }
  }, [])

  const canNext = currentIndex < maxLength

  const canBack = currentIndex > 0

  const next = useCallback(() => {
    if (canNext) {
      setCurrentIndex((prev) => prev + 1)
    }
  }, [canNext])

  const prev = useCallback(() => {
    if (canBack) {
      setCurrentIndex((prev) => prev - 1)
    }
  }, [canBack])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isActive) {
        if (event.key === 'ArrowRight') {
          next()
        } else if (event.key === 'ArrowLeft') {
          prev()
        } else if (event.key === 'Escape') {
          close()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [next, prev])

  const close = () => {
    setIsActive(false)
    loaderRef.current = null
    mediasRef.current = []
  }

  return {
    currentMedia: mediasRef.current[currentIndex],
    loader: loaderRef.current,
    canNext,
    canBack,
    isActive,
    next,
    prev,
    close,
  }
}

export default useMediaPreview
